/**
 * What an event of the staff stream makes stale on the orders screens.
 *
 * Nothing is patched in place: a row's title, its actions and its place in
 * the queue are the service's to say, so what an event touches is read again,
 * once for a burst of events.
 */
import { textOf, type LiveEvent, type Refresh } from "@/shared/live";

import { orderKey, ORDERS_KEY } from "./queries";

/** Events after which a board row reads differently: its status, who has it, its sum, its place in the queue. */
const MOVES_A_ROW: ReadonlySet<string> = new Set([
  "visit.status_changed",
  "visit.assigned",
  "visit.released",
  "visit.cancelled",
  "visit.completed",
  "visit.escalated",
  "visit.lines_changed",
]);

/** How long the list of who could be sent waits: it is long, and presence flickers. */
const CANDIDATES_DELAY_MS = 1_500;

export function ordersLive(event: LiveEvent, refresh: Refresh): void {
  // Somebody took a row of the queue, gave it back or took it over, or it left the queue:
  // the queue, the board's rows and counts, and the order's own say so.
  if (event.type.startsWith("escalation.")) {
    refresh([...ORDERS_KEY, "escalations"]);
    refresh([...ORDERS_KEY, "orders"]);
    refresh([...ORDERS_KEY, "counts"]);
    const orderId = textOf(event, "order_id");
    if (orderId !== null) refresh(orderKey(orderId));
    return;
  }
  if (event.type.startsWith("visit.")) {
    if (MOVES_A_ROW.has(event.type)) {
      refresh([...ORDERS_KEY, "orders"]);
      refresh([...ORDERS_KEY, "escalations"]);
      refresh([...ORDERS_KEY, "counts"]);
    }
    // Whatever it was, it is a line of the order's history.
    const orderId = textOf(event, "order_id");
    if (orderId !== null) refresh(orderKey(orderId));
    const visitId = textOf(event, "visit_id");
    if (visitId !== null && MOVES_A_ROW.has(event.type)) {
      refresh([...ORDERS_KEY, "candidates", visitId], CANDIDATES_DELAY_MS);
    }
    return;
  }
  // Somebody came to work, left it, or was approved or stopped: who could be sent has changed.
  if (event.type === "specialist.presence_changed" || event.type === "specialist.status_changed") {
    refresh([...ORDERS_KEY, "candidates"], CANDIDATES_DELAY_MS);
  }
}
